import React from 'react';
import { Check, Plus, Sparkles, ArrowRight, Lock, TrendingUp, Camera, ShieldCheck, BadgeDollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { packages, addOns } from '../mock';
import { toast } from 'sonner';

const PRESALE_POINTS = [
  { icon: TrendingUp, title: 'Higher offers', text: 'Buyers pay for confidence. A car that looks and smells cared-for sells faster and for more.' },
  { icon: Camera, title: 'Photos that sell', text: 'Paint correction and a spotless engine bay make your listing photos stand out from every other ad.' },
  { icon: ShieldCheck, title: 'Inspection-ready', text: 'Ceramic sealant keeps the car gleaming through every test drive and inspection.' },
  { icon: BadgeDollarSign, title: 'Pays for itself', text: 'A $549 detail routinely adds thousands to the final sale price — and weeks off the time it takes to sell.' },
];

const Services = () => {
  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  const bookPresale = () => {
    sessionStorage.setItem('quotePrefill', JSON.stringify({ package: 'Pre-Sale Detail', addons: [] }));
    scrollToBooking();
    window.dispatchEvent(new Event('quotePrefillUpdated'));
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-1.5 bg-blue-50 border border-blue-100 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-sm font-semibold text-blue-700">Detailing Packages</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
            One price. <span className="text-blue-600">Total transformation.</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
            Pick the package that fits your car's needs. Every job is hand-finished with premium products.
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24 max-w-7xl mx-auto">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`package-${pkg.id}`}
              className={`relative rounded-3xl p-8 transition-all duration-300 hover:-translate-y-2 flex flex-col ${
                pkg.popular
                  ? 'bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-2xl shadow-slate-900/30 ring-2 ring-blue-500'
                  : 'bg-white border border-slate-200 hover:shadow-2xl hover:border-blue-200'
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-4 py-1 text-xs font-bold rounded-full shadow-lg uppercase tracking-wider">
                  Most Popular
                </div>
              )}
              {pkg.locked && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-amber-400 text-white px-4 py-1 text-xs font-bold rounded-full shadow-lg uppercase tracking-wider flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Premium Customers Only
                </div>
              )}

              <div className="mb-6">
                <h3 className={`text-2xl font-extrabold mb-2 flex items-center gap-2 ${pkg.popular ? 'text-white' : 'text-slate-900'}`}>
                  {pkg.name}
                  {pkg.locked && <Lock className="w-5 h-5 text-amber-500" />}
                </h3>
                <p className={`text-sm ${pkg.popular ? 'text-slate-300' : 'text-slate-500'}`}>
                  {pkg.description}
                </p>
              </div>

              <div className={`mb-6 pb-6 border-b border-dashed ${pkg.popular ? 'border-white/20' : 'border-slate-200'}`}>
                <div className={`text-4xl font-extrabold ${pkg.popular ? 'text-white' : 'text-slate-900'}`}>
                  {pkg.startingPrice}
                </div>
                <div className={`text-xs mt-1 ${pkg.popular ? 'text-slate-400' : 'text-slate-500'}`}>
                  {pkg.priceNote || '* Varies by vehicle size & condition'}
                </div>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                      pkg.popular ? 'bg-cyan-400/20' : 'bg-blue-100'
                    }`}>
                      <Check className={`w-3 h-3 ${pkg.popular ? 'text-cyan-300' : 'text-blue-600'}`} strokeWidth={3} />
                    </div>
                    <span className={`text-sm leading-relaxed ${pkg.popular ? 'text-slate-200' : 'text-slate-700'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {pkg.locked ? (
                <Button
                  data-testid={`book-package-${pkg.id}`}
                  onClick={() => toast.info('Maintenance Detail unlocks after your first Premium Detail. Book a Premium Detail and we\'ll unlock it for you!')}
                  variant="outline"
                  className="w-full rounded-xl py-6 font-semibold border-slate-300 text-slate-500 hover:bg-slate-50"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Unlocks After Premium Detail
                </Button>
              ) : (
                <Button
                  data-testid={`book-package-${pkg.id}`}
                  onClick={scrollToBooking}
                  className="w-full rounded-xl py-6 font-semibold group bg-white text-slate-900 hover:bg-slate-100 transition-all duration-300"
                >
                  Book This Package
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Pre-Sale Pitch */}
        <div data-testid="presale-pitch" className="mb-24 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl opacity-10"></div>
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-1.5 bg-cyan-400/10 border border-cyan-400/20 rounded-full mb-6">
                <BadgeDollarSign className="w-4 h-4 text-cyan-300 mr-2" />
                <span className="text-sm font-semibold text-cyan-300">Selling Your Car?</span>
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
                A $549 detail can add <span className="text-cyan-300">thousands</span> to your sale price.
              </h3>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                First impressions decide what buyers offer. Our Pre-Sale Detail gets your car looking, smelling and photographing like it's worth more — because to a buyer, it is.
              </p>
              <Button
                data-testid="presale-cta-btn"
                onClick={bookPresale}
                className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 rounded-xl px-8 py-6 font-extrabold shadow-lg shadow-cyan-500/25 group"
              >
                Book the Pre-Sale Detail
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {PRESALE_POINTS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} data-testid={`presale-point-${i}`} className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm hover:border-cyan-400/30 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div className="font-bold text-white mb-1">{p.title}</div>
                    <div className="text-sm text-slate-400 leading-relaxed">{p.text}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Add-Ons */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
          {/* Decorative orbs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl opacity-10"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl opacity-10"></div>

          <div className="relative">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/10 rounded-full mb-4">
                <Plus className="w-4 h-4 text-cyan-300 mr-2" />
                <span className="text-sm font-semibold text-white">Add-Ons</span>
              </div>
              <h3 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">
                Customise your detail
              </h3>
              <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                Add these à la carte to any package above for the perfect finish.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {addOns.map((addOn) => (
                <div
                  key={addOn.id}
                  className="bg-white/5 backdrop-blur-md border border-white/10 p-5 rounded-2xl hover:bg-white/10 hover:border-cyan-400/30 transition-all duration-300 group"
                >
                  <div className="flex justify-between items-start gap-3 mb-2">
                    <h4 className="font-bold text-white text-base">{addOn.name}</h4>
                    <span className="text-cyan-300 font-extrabold text-lg flex-shrink-0">{addOn.price}</span>
                  </div>
                  <p className="text-slate-400 text-sm">{addOn.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Button
                data-testid="addon-book-btn"
                onClick={scrollToBooking}
                size="lg"
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl px-8 py-6 font-semibold"
              >
                Get Your Custom Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
